// Dubai guide: who pays for AC maintenance - landlord or tenant.
// Legal content is general information, not legal advice; keep it that way.
const content = {
    slug: 'ac-maintenance-landlord-or-tenant',
    title: 'Who Pays for AC Maintenance in Dubai? Landlord vs Tenant',
    description: 'Who pays for AC maintenance and repairs in a Dubai rental: what the tenancy law says, what contracts usually add, and how to handle a dispute.',
    eyebrow: 'Dubai · Renting guide',
    h1: 'Who Pays for AC Maintenance in Dubai: Landlord or Tenant?',
    lead: 'It is one of the most common arguments between Dubai tenants and landlords, usually in July when the AC stops working. The short answer: major repairs usually fall on the landlord, routine upkeep often falls on the tenant - but your contract decides the details.',
    datePublished: '2026-10-03',
    image: 'blog/living/broken-aircon.png',
    imageAlt: 'Broken AC unit in a rented Dubai apartment',
    summary: {
        title: 'In short',
        items: [
            'Dubai\'s tenancy law puts maintenance and repair of defects on the landlord unless the contract says otherwise.',
            'Many contracts make the tenant responsible for routine or minor maintenance - filter cleaning, servicing, small repairs.',
            'Major failures - compressors, motors, leaks, control boards - are normally the landlord\'s.',
            'Keep records: service reports, photos and messages. They settle most disputes.',
        ],
    },
    sections: [
        {
            title: 'What the law says',
            paragraphs: [
                'Rental relationships in Dubai are governed by Law No. 26 of 2007, as amended, overseen by RERA (the Real Estate Regulatory Agency, part of the Dubai Land Department). Under it, the landlord is generally responsible for maintaining the property and repairing defects that affect the tenant\'s use of it during the tenancy - unless the parties agree otherwise.',
                'That "unless agreed otherwise" is the important part. Tenancy contracts and their addenda can shift some maintenance to the tenant, and many do.',
            ],
            note: 'This guide is general information, not legal advice. For a specific dispute, check your contract and contact RERA or the Rental Dispute Settlement Centre.',
        },
        {
            title: 'What contracts typically say about AC',
            cards: [
                { title: 'Usually the tenant', body: 'Cleaning filters, routine AC cleaning every few months, and minor repairs - sometimes up to a stated value per repair.' },
                { title: 'Usually the landlord', body: 'Replacing failed compressors, fan motors, control boards and thermostats; fixing refrigerant leaks; replacing worn-out units.' },
                { title: 'Often disputed', body: 'Gas refills (is it a leak or neglect?), blocked drains that damaged a ceiling, and units that fail in the first weeks of a tenancy.' },
                { title: 'District cooling', body: 'Chiller fees are a separate question. In "chiller-free" buildings the landlord pays them; otherwise the tenant usually does.' },
            ],
        },
        {
            title: 'How to avoid a dispute',
            steps: [
                { title: 'At move-in', body: 'Run every unit, note any that are weak or noisy, and photograph filters and grilles. Ask the landlord for the last service date.' },
                { title: 'Keep up the routine', body: 'If routine cleaning is your responsibility, do it and keep the reports. It protects you when a major part fails.' },
                { title: 'Report problems in writing', body: 'Message the landlord or agent with photos the day a problem starts. A diagnosis report from a technician helps.' },
                { title: 'At move-out', body: 'A cleaning before handover avoids deductions from your deposit for "dirty AC".' },
            ],
        },
        {
            title: 'If the landlord won\'t fix it',
            paragraphs: [
                'Put the request in writing and give a reasonable deadline. If nothing happens, you can file a case with the Rental Dispute Settlement Centre. Avoid withholding rent - it can put you in breach of your contract. In urgent summer cases, some tenants pay for the repair and ask for reimbursement with the invoice; agree this with the landlord in writing first.',
            ],
        },
    ],
    faqs: [
        {
            question: 'Is AC cleaning the tenant\'s responsibility in Dubai?',
            answer: 'Often yes - many contracts make routine cleaning and minor maintenance the tenant\'s job. Check your contract and any maintenance addendum.',
        },
        {
            question: 'Who pays if the AC compressor fails?',
            answer: 'Normally the landlord, as a major repair, unless the failure was caused by the tenant\'s misuse or neglect.',
        },
        {
            question: 'Can my landlord deduct AC cleaning from my deposit?',
            answer: 'If the contract makes cleaning your responsibility and the units are dirty at handover, the landlord may claim the cost. A service report from shortly before move-out helps.',
        },
    ],
    cta: {
        title: 'Need a service report for your landlord?',
        body: 'Every StringsSG visit ends with a short report of what was done and anything that needs attention - useful evidence either way.',
        href: '/ae/ac-service-dubai/ac-repair',
        label: 'Book a repair diagnosis',
        bookSummary: 'AC cleaning before move-out in Dubai',
        bookLabel: 'Book a move-out cleaning',
    },
}

export default content
